// AppUsernamePrompt — the light "pick your username" nudge shown once
// after joining via an invite (INVITE-ONBOARDING-USERNAME-NOT-ORG-1).
// Deliberately SMALL and dismissable — the anti-pattern it replaces was a
// non-dismissable org-creation modal shown to people who had just joined
// an existing org. Opened through functions/usernamePrompt.js
// (openCustomModal layer, same mechanics as AppCreateOrgModal); all draft
// state lives on root (_unamePrompt*) so reactive reads survive the
// custom-template mount.
//
// Availability feedback is FEATURE-DETECTED: when the SDK exposes a
// username-availability method it runs debounced on input; otherwise the
// save path alone reports a taken name (server-side uniqueness). Never a
// hard dependency — older SDK builds keep the prompt fully usable.

const _checkAvailability = async (el, s, draft) => {
  const sdk = el.sdk
  const fn =
    (typeof sdk?.checkUsernameAvailable === 'function' && sdk.checkUsernameAvailable) ||
    (typeof sdk?.usernameAvailable === 'function' && sdk.usernameAvailable) ||
    null
  if (!fn) return
  try {
    const res = await fn.call(sdk, { username: draft })
    const available = res?.available ?? res?.data?.available
    // `reason` explains a refusal the retry advice depends on: a RESERVED word
    // is never going to free up, a taken one might. The endpoint sends it for
    // both the reserved list and a `validateUsername` format error
    // (server UserController.checkUsernameAvailability —
    // `{ success, data: { available, username, reason?, suggestion? } }`), so
    // it is carried through verbatim rather than re-worded here. Same
    // dual-envelope read as `available`: the SDK unwraps `data`, older builds
    // do not.
    const reason = res?.reason ?? res?.data?.reason
    // Stale-response guard: only apply if the draft is unchanged.
    if ((s.root?._unamePromptDraft || '').trim().toLowerCase() !== draft) return
    if (available === false) {
      s.root.update({
        _unamePromptStatus: 'taken',
        _unamePromptReason: typeof reason === 'string' && reason ? reason : null
      })
    } else if (available === true) {
      s.root.update({ _unamePromptStatus: 'available', _unamePromptReason: null })
    }
  } catch {
    /* availability is advisory — save still validates server-side */
  }
}

export const AppUsernamePrompt = {
  flow: 'y',
  gap: 'A',
  padding: 'B',
  width: '100%',
  maxWidth: 'H',

  ModalHeader: {
    Hgroup: {
      H: { text: '{{ workspace.username.title | polyglot }}' },
      P: {
        text: '{{ workspace.username.subtitle | polyglot }}'
      }
    },
    Icons: {
      Close: {
        onClick: (e, el) => {
          e.stopPropagation()
          // Backdrop-family close = "not now", not "never" — no DONE key.
          el.call('closeUsernamePrompt')
        }
      }
    }
  },

  Form: {
    tag: 'form',
    flow: 'y',
    gap: 'Z1',
    novalidate: true,

    onSubmit: (e, el, s) => {
      e.preventDefault()
      el.call('saveUsernamePrompt')
    },

    Field: {
      flow: 'y',
      gap: 'V',

      Label: {
        tag: 'label',
        text: '{{ workspace.username.label | polyglot }}',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption'
      },
      Input: {
        tag: 'input',
        type: 'text',
        name: 'username',
        'aria-label': (el) => el.call('polyglot', 'workspace.usernamePrompt.fieldLabel'),
        maxlength: 39,
        theme: 'field',
        round: 'radiusControl',
        minHeight: 'controlH',
        fontSize: 'Z2',
        padding: '0 A',
        width: '100%',
        value: (el, s) => s.root?._unamePromptDraft || '',
        onRender: (el) => {
          setTimeout(() => el.node?.focus?.(), 60)
        },
        onInput: (e, el, s) => {
          const draft = String(e.target?.value || '')
          s.root.update({
            _unamePromptDraft: draft,
            _unamePromptError: null,
            // The previous answer's reason belongs to the previous draft.
            _unamePromptReason: null,
            _unamePromptStatus: draft.trim() ? 'checking' : 'idle'
          })
          clearTimeout(el.__unameCheckT)
          const normalized = draft.trim().toLowerCase()
          if (!normalized) return
          el.__unameCheckT = setTimeout(() => _checkAvailability(el, s, normalized), 450)
        }
      },
      Hint: {
        tag: 'p',
        minHeight: 'Z2',
        fontSize: 'Y2',
        lineHeight: '1.4em',
        color: (el, s) =>
          s.root?._unamePromptError || s.root?._unamePromptStatus === 'taken' ? 'red' : 'inactive',
        text: (el, s) => {
          // A bundle key from functions/usernamePrompt.js, or the server's own
          // message — polyglot returns a non-key verbatim.
          if (s.root?._unamePromptError) return el.call('polyglot', s.root._unamePromptError)
          if (s.root?._unamePromptStatus === 'taken') {
            // The server's own words when it sent them — a reserved handle and
            // a taken one need different advice. Fallback for the plain
            // uniqueness refusal, which carries no reason.
            return s.root?._unamePromptReason || el.call('polyglot', 'workspace.username.taken')
          }
          if (s.root?._unamePromptStatus === 'available')
            return el.call('polyglot', 'workspace.username.available')
          return el.call('polyglot', 'workspace.username.rules')
        }
      }
    },

    Actions: {
      flow: 'x',
      gap: 'Z1',
      align: 'center end',

      SkipButton: {
        extends: 'Button',
        type: 'button',
        text: '{{ workspace.username.keepCurrent | polyglot }}',
        background: 'hover',
        color: 'paragraph',
        minHeight: 'controlHCompact',
        padding: '0 A',
        round: 'radiusPill',
        fontSize: 'Z',
        onClick: (e, el) => {
          e.preventDefault()
          el.call('skipUsernamePrompt')
        }
      },
      SaveButton: {
        extends: 'Button',
        theme: 'primary',
        type: 'submit',
        minHeight: 'controlHCompact',
        padding: '0 A2',
        round: 'radiusPill',
        fontSize: 'Z',
        fontWeight: '700',
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?._unamePromptBusy
              ? 'workspace.usernamePrompt.saving'
              : 'workspace.usernamePrompt.save'
          ),
        disabled: (el, s) => !!s.root?._unamePromptBusy,
        opacity: (el, s) => (s.root?._unamePromptBusy ? '.55' : '1'),
        cursor: (el, s) => (s.root?._unamePromptBusy ? 'not-allowed' : 'pointer')
      }
    }
  }
}
